// src/auth/officeSsoErrorMapper.ts
//
// Maps Office.js `Office.auth.getAccessToken()` numeric error codes
// (13000-series) to typed outcomes. See Microsoft's documented code list:
// https://learn.microsoft.com/office/dev/add-ins/develop/troubleshoot-sso-in-office-add-ins
//
// Return shape is a discriminated union so callers get exhaustive type checks:
//   - `kind: "retry"`   → the caller should retry with interactive prompt
//                         (13001 "user not signed in" on first attempt)
//   - `kind: "error"`   → surface the enclosed user-friendly Error
//   - `kind: "unknown"` → code isn't in the known table; caller re-throws
//                         the original error for parent retry logic

export type OfficeSsoErrorResult =
  | { kind: "retry" }
  | { kind: "error"; error: Error }
  | { kind: "unknown" };

/** Build a user-friendly error for a specific Office.js error code. */
function makeError(code: number): Error {
  switch (code) {
    case 13001: // User not signed in (with prompt already attempted)
      return new Error("Please sign in to your Office 365 account to use all features.");
    case 13002: // User aborted sign-in
      return new Error("Sign-in was cancelled. Some features may be limited.");
    case 13003: // Unsupported account type (e.g., on-premises domain account)
      return new Error(
        "This account type is not supported. Please use an Office 365 work or school account."
      );
    case 13004: // Invalid resource
      return new Error("Configuration error. Please contact support");
    case 13005: // Invalid grant
      return new Error("Authorization error. Please try signing out and back in");
    case 13006: // Client error (web only)
      return new Error(
        "Browser session error. Please sign out of Office and restart your browser."
      );
    case 13007: // Token acquisition failure
      return new Error(
        "Unable to get authentication token. Please verify your account permissions."
      );
    case 13008: // Concurrent request
      return new Error("Another sign-in is in progress. Please wait and try again");
    case 13012: // Unsupported platform
      return new Error("Authentication is not supported on this platform");
    case 13013: // Request throttled
      return new Error("Too many authentication attempts. Please wait a moment and try again.");
    default:
      // Caller handles via "unknown" kind; this branch is unreachable.
      return new Error(`Unknown Office SSO error code: ${code}`);
  }
}

/**
 * Translate an Office SSO error code into a typed outcome.
 *
 * @param code Numeric error code from Office.js (13xxx).
 * @param allowPrompt Whether this call already allowed interactive prompt.
 *   When `false` and code is 13001 ("user not signed in"), returns `"retry"`
 *   so the caller can re-invoke with `allowSignInPrompt: true`.
 */
export function mapOfficeSsoError(code: number, allowPrompt: boolean): OfficeSsoErrorResult {
  if (code === 13001 && !allowPrompt) {
    return { kind: "retry" };
  }
  // 13000 ("SSO not supported") and any code not in the known table.
  if (code === 13000) return { kind: "unknown" };
  // Known codes → user-friendly Error.
  const known = new Set([13001, 13002, 13003, 13004, 13005, 13006, 13007, 13008, 13012, 13013]);
  if (known.has(code)) return { kind: "error", error: makeError(code) };
  return { kind: "unknown" };
}
